import { useState, useCallback } from "react";
import { Mail, Download, Search, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { useCommerceApi, useLoad, Loading, EmptyState, Panel, DataTable, fmtDate } from "../commerce/shared";

const BASE = "/admin/pulse/launch-subscribers";

const Stat = ({ label, value, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value ?? "—"}</p>
  </div>
);

const Chart = ({ series }) => {
  const max = Math.max(1, ...series.map(s => s.count));
  return (
    <div className="flex items-end gap-1 h-28 px-5 py-4" data-testid="pulse-subs-chart">
      {series.map(s => (
        <div key={s.date} title={`${s.date}: ${s.count}`} className="flex-1 bg-indigo-500/80 rounded-t" style={{ height: `${Math.max(2, (s.count / max) * 100)}%` }} />
      ))}
    </div>
  );
};

const STATUS_STYLE = { active: "bg-emerald-50 text-emerald-700", unsubscribed: "bg-slate-100 text-slate-600", suppressed: "bg-rose-50 text-rose-700" };

export const PulseLaunchSubscribers = ({ token }) => {
  const call = useCommerceApi(token);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const path = `${BASE}?page=${page}&limit=25${q ? `&q=${encodeURIComponent(q)}` : ""}${status ? `&status=${status}` : ""}`;
  const rawCall = useCallback((p) => call(p, { raw: true }), [call]);
  const list = useLoad(rawCall, path);
  const stats = useLoad(rawCall, `${BASE}/stats`);

  const suppress = async (email) => {
    if (!window.confirm(`Suppress ${email}? They will be excluded from all PULSE launch emails.`)) return;
    await call(`${BASE}/${encodeURIComponent(email)}/suppress`, { method: "POST", raw: true });
    toast.success("Subscriber suppressed");
    list.reload(); stats.reload();
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}${BASE}/export?status=${status || "active"}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    if (!res.ok) return toast.error("Export failed");
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: "pulse-launch-subscribers.csv" });
    a.click(); URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "email", label: "Email" },
    { key: "subscribed_at", label: "Subscribed", render: r => fmtDate(r.subscribed_at) },
    { key: "consent", label: "Consent", render: r => <span className="text-xs text-slate-600">{r.consent_version} · {fmtDate(r.consent_at)}</span> },
    { key: "status", label: "Status", render: r => <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}{r.unsubscribed_at ? ` · ${fmtDate(r.unsubscribed_at)}` : ""}</span> },
    { key: "actions", label: "", render: r => r.status === "active" && <Button size="sm" variant="ghost" className="text-rose-600 h-7" onClick={() => suppress(r.email)} data-testid={`pulse-sub-suppress-${r.email}`}><Ban className="h-3.5 w-3.5 mr-1" />Suppress</Button> },
  ];
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.limit)) : 1;

  return (
    <div className="space-y-6" data-testid="pulse-launch-subscribers">
      <div>
        <h2 className="text-xl font-bold text-slate-900">PULSE · Launch Subscribers</h2>
        <p className="text-sm text-slate-500">People who opted in to PULSE launch updates. Stored separately from CRM leads.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Active subscribers" value={stats.data?.active} testid="pulse-subs-active" />
        <Stat label="New in last 30 days" value={stats.data?.last_30_days} testid="pulse-subs-30d" />
        <Stat label="Unsubscribed / suppressed" value={stats.data?.inactive} testid="pulse-subs-inactive" />
      </div>
      {stats.data && <Panel title="Sign-ups, last 30 days"><Chart series={stats.data.series} /></Panel>}
      <Panel title="Subscribers" action={<Button size="sm" variant="outline" onClick={exportCsv} data-testid="pulse-subs-export"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>}>
        <div className="flex flex-wrap gap-3 px-5 py-4 border-b border-slate-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search email" className="pl-9" data-testid="pulse-subs-search" />
          </div>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="border border-slate-200 rounded-md px-3 text-sm" data-testid="pulse-subs-status">
            <option value="">All statuses</option><option value="active">Active</option><option value="unsubscribed">Unsubscribed</option><option value="suppressed">Suppressed</option>
          </select>
        </div>
        {list.loading ? <Loading /> : !list.data?.items.length ? (
          <div className="p-5"><EmptyState icon={Mail} title="No subscribers yet" text="Sign-ups from the PULSE page will appear here." testid="pulse-subs-empty" /></div>
        ) : (
          <>
            <DataTable columns={columns} rows={list.data.items} rowKey="email" testid="pulse-subs-table" />
            <div className="flex items-center justify-between px-5 py-3 text-sm text-slate-500">
              <span>{list.data.total} total</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="pulse-subs-prev">Previous</Button>
                <span className="px-2 py-1">{page} / {pages}</span>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="pulse-subs-next">Next</Button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};
